import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  onAuthStateChanged,
  signInWithPhoneNumber,
  signInWithPopup,
  GoogleAuthProvider,
  RecaptchaVerifier,
  signOut,
  ConfirmationResult,
  User as FirebaseUser,
} from "firebase/auth";
import { auth } from "../config/firebase.ts";

// ══════════════════════════════════════
// Tipi
// ══════════════════════════════════════

interface UserProfile {
  uid: string;
  displayName: string;
  email: string;
  phone: string;
  phoneVerified: boolean;
  profileComplete: boolean;
  authProviders: string[];
  createdAt?: string;
  updatedAt?: string;
}

interface ProfileUpdateData {
  displayName?: string;
  email?: string;
  phone?: string;
}

interface ProfileUpdateResult {
  message: string;
  profile: UserProfile;
  profileComplete: boolean;
}

interface AuthContextValue {
  user: FirebaseUser | null;
  profile: UserProfile | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  isPhoneVerified: boolean;
  isProfileComplete: boolean;
  getToken: () => Promise<string | null>;
  sendOtp: (
    phoneNumber: string,
    recaptchaContainerId?: string,
  ) => Promise<ConfirmationResult>;
  verifyOtp: (
    confirmationResult: ConfirmationResult,
    code: string,
  ) => Promise<FirebaseUser>;
  loginWithGoogle: () => Promise<FirebaseUser>;
  logout: () => Promise<void>;
  updateProfile: (data: ProfileUpdateData) => Promise<ProfileUpdateResult>;
  fetchProfile: () => Promise<void>;
}

// ══════════════════════════════════════
// Context
// ══════════════════════════════════════

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth deve essere dentro AuthProvider");
  return ctx;
}

// ══════════════════════════════════════
// Provider
// ══════════════════════════════════════

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const recaptchaRef = useRef<RecaptchaVerifier | null>(null);

  // ── Ascolta cambio auth Firebase ──
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        await loadProfile(firebaseUser);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  // ── Prendi token per le API ──
  const getToken = useCallback(async (): Promise<string | null> => {
    if (!user) return null;
    try {
      return await user.getIdToken();
    } catch {
      return null;
    }
  }, [user]);

  // ── Carica profilo dal backend ──
  const loadProfile = async (firebaseUser: FirebaseUser): Promise<void> => {
    try {
      const token = await firebaseUser.getIdToken();
      const res = await fetch(
        `${process.env.REACT_APP_API_URL_NODE}/api/trail-buddy/profile/me`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.ok) {
        const data = await res.json();
        if (data.exists) setProfile(data as UserProfile);
      }
    } catch (err) {
      console.error("Errore caricamento profilo:", err);
    }
  };

  // ── Login con telefono (OTP) ──
  const sendOtp = async (
    phoneNumber: string,
    recaptchaContainerId: string = "recaptcha-container",
  ): Promise<ConfirmationResult> => {
    setError(null);
    try {
      // Crea recaptcha se non esiste
      if (!recaptchaRef.current) {
        recaptchaRef.current = new RecaptchaVerifier(
          auth,
          recaptchaContainerId,
          {
            size: "invisible",
            callback: () => {},
          },
        );
      }

      const confirmation = await signInWithPhoneNumber(
        auth,
        phoneNumber,
        recaptchaRef.current,
      );
      return confirmation;
    } catch (err: any) {
      console.error("Errore invio OTP:", err);
      // Reset recaptcha in caso di errore
      if (recaptchaRef.current) {
        recaptchaRef.current.clear();
        recaptchaRef.current = null;
      }
      const msg = err?.message || "Errore invio OTP";
      setError(msg);
      throw err;
    }
  };

  const verifyOtp = async (
    confirmationResult: ConfirmationResult,
    code: string,
  ): Promise<FirebaseUser> => {
    setError(null);
    try {
      const result = await confirmationResult.confirm(code);
      setUser(result.user);
      await loadProfile(result.user);
      return result.user;
    } catch (err) {
      console.error("Errore verifica OTP:", err);
      setError("Codice non valido");
      throw err;
    }
  };

  // ── Login con Google ──
  const loginWithGoogle = async (): Promise<FirebaseUser> => {
    setError(null);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      setUser(result.user);
      await loadProfile(result.user);
      return result.user;
    } catch (err: any) {
      console.error("Errore login Google:", err);
      setError(err?.message || "Errore login Google");
      throw err;
    }
  };

  // ── Logout ──
  const logout = async (): Promise<void> => {
    await signOut(auth);
    setUser(null);
    setProfile(null);
  };

  // ── Aggiorna profilo ──
  const updateProfile = async (
    data: ProfileUpdateData,
  ): Promise<ProfileUpdateResult> => {
    const token = await getToken();
    const res = await fetch(
      `${process.env.REACT_APP_API_URL_NODE}/api/trail-buddy/profile`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      },
    );

    const result = await res.json();
    if (res.ok && result.profile) {
      setProfile(result.profile as UserProfile);
    }
    return result as ProfileUpdateResult;
  };

  // ── Fetch profilo pubblico ──
  const fetchProfile = async (): Promise<void> => {
    if (user) await loadProfile(user);
  };

  const value: AuthContextValue = {
    user,
    profile,
    loading,
    error,
    isAuthenticated: !!user,
    isPhoneVerified: !!user?.phoneNumber,
    isProfileComplete: !!profile?.profileComplete,
    getToken,
    sendOtp,
    verifyOtp,
    loginWithGoogle,
    logout,
    updateProfile,
    fetchProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
